/**
 * รับเข้าสต๊อก (Goods Receipt) — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Goods Receipt — Payload Contract" และหัวข้อย่อย
 * "รูปร่างคำตอบของ `GET` และของ `POST`")
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง ต่างจาก
 * `lib/productionOrders/types.ts` ตรงที่ **คำตอบของกลุ่มนี้ถูก freeze แล้วจริง ๆ**
 * — เอกสารระบุว่าถอดมาจาก `modules/inventory/schemas.py` ที่ `WO-P1-M4B-001`
 * สร้างขึ้น ไม่ใช่จากความทรงจำ ชนิดข้อมูลด้านล่างจึงบังคับทุกฟิลด์ที่ contract
 * บังคับ แทนที่จะปล่อยเป็น nullable เพื่อกันความไม่แน่นอนที่ไม่มีอยู่แล้ว
 *
 * ⚠️ `quantity` เป็น **string** ทั้งขาเข้าและขาออก `NUMERIC(18,4)` ที่ผ่าน
 * IEEE-754 double คือจำนวนที่เพี้ยนไปเงียบ ๆ และ 250.0000 สำคัญเมื่อมันคือเหล็ก
 */

/** รหัสโมดูลของกลุ่มนี้ใน RBAC matrix — ค่าเดียวกับที่ contract ใช้กับทุก endpoint */
export const INVENTORY_MODULE = 'INVENTORY';

/**
 * Header ที่ contract บังคับบน `POST /v1/goods-receipts` — ไม่มีตอบ `400`
 *
 * ประกาศไว้ที่เดียวเพื่อให้ client กับเทสต์สะกดเหมือนกันเสมอ ชื่อ header ที่
 * พิมพ์ผิดหนึ่งตัวคือ endpoint ที่กลายเป็นไม่ idempotent โดยไม่มีใครเห็น
 */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';

/* ------------------------------------------------------------ quality ---- */

/**
 * สถานะของ lot ที่ Goods Receipt สร้าง — **เริ่มที่ `QUALITY_HOLD` เสมอ** (D-059)
 *
 * ค่านี้ไม่เคยเดินทางไปกับ request มันมาจากเซิร์ฟเวอร์อย่างเดียว และมีอยู่ที่นี่
 * เพื่อให้หน้าจอ**พูดถึงมันได้** ไม่ใช่เพื่อให้เลือกได้: ของที่รับเข้ามาแล้วยัง
 * เบิกไปใช้ไม่ได้ และหน้าจอที่รายงานความสำเร็จเฉย ๆ โดยไม่บอกเรื่องนี้ กำลังสอน
 * พนักงานคลังว่า "รับเข้าแล้ว" แปลว่า "ใช้ได้แล้ว"
 */
export const QUALITY_HOLD = 'QUALITY_HOLD';

/**
 * สถานะอื่นที่ `POST /v1/lots/{lot_id}/quality-decision` (`WO-P1-M4B-002`)
 * เปลี่ยนไปได้ — receipt เก่าที่ถูกตัดสินไปแล้วอ่านกลับมาเป็นค่าเหล่านี้
 *
 * มีไว้เพื่อ **แสดงผล** เท่านั้น หน้าจอนี้ไม่มีปุ่มที่เปลี่ยนค่าใดในชุดนี้
 */
export const QUALITY_RELEASED = 'RELEASED';
export const QUALITY_REJECTED = 'REJECTED';
export const QUALITY_BLOCKED = 'BLOCKED';

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `422` เมื่อ product+plant นั้นไม่มี `unit_cost` ให้ตีมูลค่า
 *
 * contract เขียนไว้ตรง ๆ ว่า **ห้ามโพสต์ด้วยค่าศูนย์** — เอกสารบัญชีที่มูลค่าศูนย์
 * คือของที่หายไปจากงบ คนที่แก้ข้อนี้ได้คือผู้ดูแลข้อมูลต้นทุน ไม่ใช่พนักงานคลัง
 */
export const NO_UNIT_COST = 'NO_UNIT_COST';

/**
 * `422` เมื่อไม่มีแถวใน `movement_type_account_mapping` ของ movement type นี้
 *
 * ⚠️ **คนละคนแก้กับ `NO_UNIT_COST`** — อันนั้นคือต้นทุนสินค้า อันนี้คือผังบัญชี
 * ข้อความรวมว่า "บันทึกไม่สำเร็จ" ส่งคนไปหาผิดแผนกทั้งสองกรณี
 */
export const NO_ACCOUNT_MAPPING = 'NO_ACCOUNT_MAPPING';

/** `422` เมื่อ `fiscal_period` ของ `posting_date` ไม่ได้อยู่ในสถานะ `OPEN` */
export const FISCAL_PERIOD_CLOSED = 'FISCAL_PERIOD_CLOSED';

/** `422` เมื่อที่เก็บที่เลือกไม่ได้อยู่ใน Plant เดียวกับที่ระบุใน payload */
export const STORAGE_LOCATION_NOT_IN_PLANT = 'STORAGE_LOCATION_NOT_IN_PLANT';

/** `422` เมื่อสินค้ายังไม่ `ACTIVE` หรือโรงงานนั้นยังไม่มีข้อมูลระดับโรงงานของมัน */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/**
 * `409` เมื่อ `Idempotency-Key` เดิมถูกใช้กับ **body ที่ต่างออกไป**
 *
 * นี่คือ bug ของ client ไม่ใช่เหตุการณ์ปกติ และเป็นเหตุผลที่หน้าจอนี้ผูก key
 * ไว้กับเนื้อของคำขอ (ดู `idempotency.ts`) การกลืนมันคือการทิ้งของจริงไปเงียบ ๆ
 */
export const IDEMPOTENCY_KEY_REUSED = 'IDEMPOTENCY_KEY_REUSED';

/**
 * กฎทุกข้อที่ `docs/API_CONTRACT.md` เขียนไว้ตรง ๆ สำหรับกลุ่มนี้
 *
 * **ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุ** — รหัสที่หน้าจอคิดขึ้นเองคือข้อความที่
 * ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
export const GOODS_RECEIPT_RULES = [
  NO_UNIT_COST,
  NO_ACCOUNT_MAPPING,
  FISCAL_PERIOD_CLOSED,
  STORAGE_LOCATION_NOT_IN_PLANT,
  PRODUCT_NOT_ACTIVE,
  IDEMPOTENCY_KEY_REUSED,
] as const;

export type GoodsReceiptRule = (typeof GOODS_RECEIPT_RULES)[number];

/* -------------------------------------------------------------- resource -- */

/**
 * ใบรับเข้าหนึ่งใบตามที่เซิร์ฟเวอร์ส่งกลับมา — รูปเดียวกันทั้ง `POST`, `GET` ใบเดียว
 * และแต่ละ item ในรายการ (contract ระบุว่า "ไม่ตัดฟิลด์ใดออก")
 *
 * `lot_id` และ `quality_status` อยู่บน receipt เพราะ receipt ถูกอ่านเพื่อตอบว่า
 * "ของเข้าแล้วหรือยัง และใช้ได้หรือยัง" ซึ่งเป็นคำถามเดียวกันในใจผู้ใช้ — ไม่ต้อง
 * ยิงคำขอที่สอง
 *
 * `supplier_lot_id` เป็น `null` ได้ ของที่มาโดยไม่มีเลขล็อตของผู้ขายเป็นเรื่องปกติ
 * และเป็นฟิลด์เดียวในชุดนี้ที่ contract อนุญาตให้ว่าง
 */
export interface GoodsReceipt {
  readonly receipt_id: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly storage_location_id: string;
  readonly storage_location_code: string;
  /** lot ที่ใบนี้สร้าง — หนึ่ง lot ต่อหนึ่ง receipt และใช้ประกอบ URL ของ quality-decision */
  readonly lot_id: string;
  /** **เซิร์ฟเวอร์เป็นเจ้าของ** — `QUALITY_HOLD` เสมอสำหรับใบที่เพิ่งสร้าง (D-059) */
  readonly quality_status: string;
  readonly supplier_lot_id: string | null;
  /** `NUMERIC(18,4)` — string เสมอ ห้ามแปลงเป็น number ไม่ว่าจะเพื่ออะไร */
  readonly quantity: string;
  /** มี offset ของโรงงานติดมาเสมอ (`+07:00`) ไม่ใช่ `Z` และไม่ใช่เวลาของเครื่องผู้ใช้ */
  readonly posting_date: string;
  readonly created_by_employee_id: string;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/goods-receipts` — envelope ตามกติกาข้ามกลุ่ม ไม่ใช่ array เปล่า */
export interface GoodsReceiptListPage {
  readonly items: readonly GoodsReceipt[];
  readonly next_cursor: string | null;
}

/**
 * Body ของ `POST /v1/goods-receipts` — หกคีย์ ไม่มีคีย์ที่เจ็ด
 *
 * **ไม่มี `quality_status`** โดยชนิดข้อมูล ไม่ใช่โดยความตั้งใจของโค้ดที่ประกอบ
 * request (D-059): field ที่ client เลือกได้คือช่องข้ามการกักกัน และการกักที่
 * ข้ามได้ด้วยการส่งค่าหนึ่งใน request ไม่ใช่การกัก — เซิร์ฟเวอร์ตอบ `400` ถ้ามัน
 * หลุดไป และฟอร์มนี้ไม่มีทางประกอบมันขึ้นมาได้เลย
 *
 * เช่นเดียวกับ `receipt_id`, `lot_id`, `created_by_employee_id`, `movement_type`,
 * `unit_cost` และ `amount` ซึ่งเป็นของเซิร์ฟเวอร์ทั้งชุด
 */
export interface CreateGoodsReceiptBody {
  /** บังคับเสมอ และเป็นขอบเขตสิทธิ์ (D-004) — นอก scope เซิร์ฟเวอร์ตอบ `403` */
  readonly plant_code: string;
  readonly product_code: string;
  /** unique ภายใน Plant เดียว จึงถูกตรวจคู่กับ `plant_code` เสมอ */
  readonly storage_location_code: string;
  /** `NUMERIC` — string เสมอ ต้องมากกว่า 0 */
  readonly quantity: string;
  /** ต้องมี offset ของโรงงาน — ประกอบจาก `lib/businessTime.ts` เท่านั้น (D-055) */
  readonly posting_date: string;
  /** เลขล็อตของผู้ขาย — `null` ได้ ของที่มาโดยไม่มีเลขล็อตเป็นเรื่องปกติ */
  readonly supplier_lot_id: string | null;
}

/**
 * ตัวกรองของ `GET /v1/goods-receipts`
 *
 * ⚠️ `plant_code` ที่นี่เป็น **ตัวกรองที่ไม่บังคับ** ต่างจากบน `POST` ที่บังคับ —
 * การกรองตาม plant scope ของผู้เรียกเกิดขึ้นเสมออยู่แล้ว และการบังคับส่งจะทำให้
 * พนักงานที่ถือสองโรงงานดูรวมทั้งสองแห่งไม่ได้ทั้งที่มีสิทธิ์
 */
export interface GoodsReceiptListFilters {
  readonly plant_code?: string;
  readonly product_code?: string;
  readonly storage_location_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}
